import { useState } from "react";
import { router } from "@inertiajs/react";
import styled from "styled-components";
import { ArrowLeft, Loader } from "lucide-react";
import { useLanguage } from "../../../../contexts/language";
import { useOrder } from "../../../../contexts/order";
import { useRoom } from "../../../../contexts/room";
import {
  saveOrderToLocalHistory,
  getStoredWebPushSubscriptionId,
  setStoredWebPushSubscriptionId,
} from "../../../../util";

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
`;

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateContent = styled.div`
  text-align: center;
`;

const EmptyStateText = styled.p`
  color: #666;
  margin-bottom: 16px;
`;

const EmptyStateButton = styled.button`
  padding: 8px 16px;
  background-color: #c9a961;
  color: #fff;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(201, 169, 97, 0.9);
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid #e5e5e5;
  background-color: #fff;
`;

const HeaderContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 16px;
`;

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #c9a961;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }
`;

const GuestInfo = styled.div`
  text-align: right;
`;

const GuestName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const RoomNumber = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
`;

const MainContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 32px 16px;
`;

const PageTitle = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 24px;
  background-color: #fff;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const OrderItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 256px;
  overflow-y: auto;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const OrderItemName = styled.span`
  color: #666;
`;

const OrderItemPrice = styled.span`
  color: #333;
  font-weight: 500;
`;

const ItemNoteInput = styled.textarea`
  margin-top: 6px;
  width: 100%;
  min-height: 52px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #555;
  resize: vertical;
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentMethodLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  span {
    color: #333;
    font-weight: 500;
  }
`;

const CardForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormField = styled.div``;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: calc(100% - 32px);
  padding: 8px 16px;
  background-color: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 2px rgba(201, 169, 97, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const RightColumn = styled.div``;

const StickyCard = styled(Card)`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #c9a961;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #c9a961;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: rgba(201, 169, 97, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e5e5;
  color: #333;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function CheckoutPage(props) {
  const { building_code, web_push_vapid_public_key } = props;
  const { t } = useLanguage();
  const { cart, getCartTotal, processPayment, isProcessing, updateCartItemNote } = useOrder();
  const { roomNumber, guestName } = useRoom();
  const [userName, setUserName] = useState(guestName || "");
  const [deliveryNote, setDeliveryNote] = useState("");

  if (cart.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateContent>
          <EmptyStateText>{t.order.cart.empty}</EmptyStateText>
          <EmptyStateButton onClick={() => window.history.back()}>
            {t.order.backButton}
          </EmptyStateButton>
        </EmptyStateContent>
      </EmptyStateContainer>
    );
  }

  const TAX_RATE = 0;
  const subtotal = getCartTotal();
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;
  const formatAmount = (amount) => `Rp${Math.round(Number(amount || 0)).toLocaleString("id-ID")}`;

  const handlePayment = async () => {
    try {
      const webPushSubscriptionId =
        getStoredWebPushSubscriptionId(building_code) ?? (await ensurePushSubscriptionId());
      const order = await processPayment({
        userName,
        deliveryNote,
        buildingCode: building_code,
        webPushSubscriptionId: webPushSubscriptionId || undefined,
      });
      if (order?.id) {
        saveOrderToLocalHistory({ buildingCode: building_code, order });
        router.visit(`/hebe/${building_code}/hgs/orders/${order.id}?auto_pay=1`);
      }
    } catch (error) {
      window.alert(error?.message || "Failed to create order");
    }
  };

  const ensurePushSubscriptionId = async () => {
    if (!web_push_vapid_public_key) return null;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return null;
    try {
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission !== "granted") return null;

      const registration = await navigator.serviceWorker.register("/service-worker.js");
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(web_push_vapid_public_key),
        });
      }
      const json = subscription.toJSON();
      const response = await fetch("/api/v1/web_push_subscriptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          building_code: building_code,
          endpoint: json.endpoint,
          p256dh_key: json.keys?.p256dh,
          auth_key: json.keys?.auth,
        }),
      });
      const payload = await response.json();
      const id = payload?.data?.id ?? null;
      if (id) setStoredWebPushSubscriptionId(building_code, id);
      return id;
    } catch (_e) {
      return null;
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <HeaderInner>
            <BackButton onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
              <span>{t.order.backButton}</span>
            </BackButton>
            {guestName && roomNumber && (
              <GuestInfo>
                <GuestName>{guestName}</GuestName>
                <RoomNumber>Room {roomNumber}</RoomNumber>
              </GuestInfo>
            )}
          </HeaderInner>
        </HeaderContent>
      </Header>

      <MainContent>
        <PageTitle>{t.order.payment.title}</PageTitle>
        {guestName && roomNumber && (
          <PageSubtitle>
            {t.room.guestInfo}: {guestName} • {t.room.roomNumber} {roomNumber}
          </PageSubtitle>
        )}

        <ContentGrid>
          <LeftColumn>
            <Card>
              <CardTitle>Order Summary</CardTitle>
              <OrderItemsContainer>
                {cart.map((item) => (
                  <OrderItem key={item.id}>
                    <div>
                      <OrderItemName>
                        {item.name} x {item.quantity}
                      </OrderItemName>
                      <ItemNoteInput
                        value={item.note || ""}
                        onChange={(event) => updateCartItemNote(item.id, event.target.value)}
                        maxLength={200}
                        placeholder="Item note (max 200 chars)"
                      />
                    </div>
                    <OrderItemPrice>
                      {formatAmount(Number(item?.price?.amount || 0) * item.quantity)}
                    </OrderItemPrice>
                  </OrderItem>
                ))}
              </OrderItemsContainer>
            </Card>

            <Card>
              <CardTitle>Order Details</CardTitle>
              <CardForm>
                <FormField>
                  <FormLabel>Your Name</FormLabel>
                  <FormInput
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Delivery Note (Optional)</FormLabel>
                  <FormInput
                    type="text"
                    value={deliveryNote}
                    onChange={(e) => setDeliveryNote(e.target.value)}
                    placeholder="Any note for kitchen/staff"
                  />
                </FormField>
              </CardForm>
            </Card>
          </LeftColumn>

          <RightColumn>
            <StickyCard>
              <CardTitle>{t.order.cart.total}</CardTitle>
              <SummarySection>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.subtotal}</SummaryLabel>
                  <SummaryValue>
                    {formatAmount(subtotal)}
                  </SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.tax}</SummaryLabel>
                  <SummaryValue>{formatAmount(tax)}</SummaryValue>
                </SummaryRow>
              </SummarySection>
              <TotalRow>
                <TotalLabel>{t.order.cart.total}</TotalLabel>
                <TotalValue>{formatAmount(total)}</TotalValue>
              </TotalRow>

              <ConfirmButton onClick={handlePayment} disabled={isProcessing}>
                {isProcessing && <Loader size={16} />}
                {isProcessing
                  ? t.order.payment.processing
                  : t.order.payment.confirm}
              </ConfirmButton>

              <CancelButton
                onClick={() => window.history.back()}
                disabled={isProcessing}
              >
                {t.order.payment.cancel}
              </CancelButton>
            </StickyCard>
          </RightColumn>
        </ContentGrid>
      </MainContent>
    </PageContainer>
  );
}
